import React, { forwardRef, useRef, useState, useImperativeHandle, useMemo, useCallback } from "react";
import { Components, Types, Services } from "@sendum/core";
import s from "./Pallet.module.scss";
import toast from "react-hot-toast";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { PalletSelectorRootState } from "../store/PalletSelectorStore";
import * as PalletSelectorActions from "../store/PalletSelectorActions";

export interface IConfirmSetDriverRef {
	open: (pallet: Types.Pallet) => void;
	close: () => void;
}

interface IProps {
	onConfirmed: (palletId: string) => void;
}

export const ConfirmSetDriver = forwardRef<IConfirmSetDriverRef, IProps>(({ onConfirmed }, ref) => {
	const dispatch = useDispatch();
	const account = useSelector<PalletSelectorRootState, Types.Account | undefined>((state: PalletSelectorRootState) => state.PalletSelector.account, shallowEqual);

	const [msg, setMsg] = useState<string>("");
	const [pallet, setPallet] = useState<Types.Pallet>();
	const [isLoading, setLoading] = useState<boolean>(false);

	const modalRef = useRef<Components.IModalRef>(null);

	const confirm = useCallback(() => {
		if (!pallet) {
			return;
		}
		setLoading(true);
		Services.API.WidgetsApi.updatePallet(pallet.PalletId, { AssignedDriver: account?.UserName })
			.then(() => {
				toast.success(`Driver ${account?.UserName ?? ""} has claimed Pallet ${pallet.Batch.BatchNumber} ${pallet.PalletNumber}`, { duration: 3000 });
				onConfirmed(pallet.PalletId);
				setLoading(false);
				modalRef.current?.close();
			})
			.catch(error => {
				toast.error((error as Error).message);
				modalRef.current?.close();
			});
	}, [onConfirmed, pallet, account]);

	const actions = useMemo<Components.IAction[]>(() => [
		{ title: "Confirm", variant: "primary", func: confirm },
		{ title: "Cancel", variant: "cancel", func: () => {
			modalRef.current?.close();
			dispatch(PalletSelectorActions.setPallet(undefined));
		} },
	], [confirm]);

	useImperativeHandle(ref, () => ({
		open(_pallet: Types.Pallet) {
			if (!_pallet.AssignedDriver?.UserName) {
				setMsg("No driver currently assigned to this pallet. Would you like to take ownership of this pallet");
			} else {
				setMsg(`Driver ${_pallet.AssignedDriver?.DisplayName} is currently assigned to this pallet. Would you like to take ownership of this pallet`);
			}
			setPallet(_pallet);
			setLoading(false);
			modalRef.current?.open();
		},
		close() {
			modalRef.current?.close();
		},
	}));

	return (
		<Components.Modal ref={modalRef} title="Change driver" actions={actions} width={400} testId="change-driver-modal">
			<div className={`${s.Pallet} ${s.Pallet__inModal}`}>
				{isLoading && (<Components.Loader className={s.Pallet__loader}/>)}
				<div style={{ margin: "16px 16px 0 16px" }}>{msg}</div>
			</div>
		</Components.Modal>
	);
});
